const MAX_HIGHLIGHTS = 6;
/** An idle stretch must be this long, and this share of the session, to matter. */
const IDLE_MIN_MS = 30_000;
const IDLE_MIN_SHARE = 0.2;
export function buildHighlights(markers, durationMs) {
    const sorted = [...markers].sort((a, b) => a.offsetMs - b.offsetMs);
    const out = [];
    /** The page the visitor was on at a given moment, for context in labels. */
    const pageAt = (offsetMs) => {
        let page;
        for (const m of sorted) {
            if (m.offsetMs > offsetMs)
                break;
            if (m.type === 'navigation')
                page = m.label;
        }
        return page;
    };
    // Rage clicks: frustration made visible — every cluster earns a highlight.
    for (const m of sorted) {
        if (m.type !== 'rageClick')
            continue;
        const page = pageAt(m.offsetMs);
        out.push({ offsetMs: m.offsetMs, kind: 'rage', label: page ? `Rage clicks on ${page}` : 'Rage clicks' });
    }
    // Errors: the first occurrence of each distinct error. Repeats of the same
    // message add noise, not information — the first one is where to look.
    const seenErrors = new Set();
    for (const m of sorted) {
        if (m.type !== 'error')
            continue;
        if (seenErrors.has(m.label))
            continue;
        seenErrors.add(m.label);
        out.push({ offsetMs: m.offsetMs, kind: 'error', label: m.label });
    }
    // The longest dead stretch, when it dominates the session: a visitor staring
    // at (or abandoning) a page is a finding, and scrubbing never surfaces it.
    if (durationMs > 0 && sorted.length > 0) {
        let gapStart = 0;
        let gapLen = 0;
        let prev = 0;
        for (const m of sorted) {
            if (m.offsetMs - prev > gapLen) {
                gapLen = m.offsetMs - prev;
                gapStart = prev;
            }
            prev = m.offsetMs;
        }
        if (durationMs - prev > gapLen) {
            gapLen = durationMs - prev;
            gapStart = prev;
        }
        if (gapLen >= IDLE_MIN_MS && gapLen / durationMs >= IDLE_MIN_SHARE) {
            const page = pageAt(gapStart);
            out.push({
                offsetMs: gapStart,
                kind: 'idle',
                label: `${Math.round(gapLen / 1000)}s of inactivity${page ? ` on ${page}` : ''}`,
            });
        }
    }
    // Where they gave up: the last page, when the session visited more than one.
    const navs = sorted.filter((m) => m.type === 'navigation');
    if (navs.length >= 2) {
        const last = navs[navs.length - 1];
        out.push({ offsetMs: last.offsetMs, kind: 'exit', label: `Left from ${last.label}` });
    }
    // Chronological, deduplicated by moment, capped. When over the cap, errors
    // and rage outrank idle/exit — they are why someone is watching at all.
    const rank = { rage: 0, error: 1, idle: 2, exit: 3 };
    const kept = out
        .sort((a, b) => rank[a.kind] - rank[b.kind])
        .slice(0, MAX_HIGHLIGHTS)
        .sort((a, b) => a.offsetMs - b.offsetMs);
    const seen = new Set();
    return kept.filter((h) => {
        const key = Math.round(h.offsetMs / 500); // two in the same half-second are one moment
        if (seen.has(key))
            return false;
        seen.add(key);
        return true;
    });
}
//# sourceMappingURL=highlights.js.map